import { prefersReducedMotion } from './motion.js'

const FALLBACK_DURATION_MS = 450
const SCROLL_SPEED = 0.7

/** 브라우저 기본 이동 시간의 1/0.7배 동안 본문으로 이동한다. 직접 입력하면 양보한다. */
export function scrollToContent(target: HTMLElement, nativeDurationMs: number): () => void {
  const startY = window.scrollY
  if (document.hidden || target.getBoundingClientRect().top <= 1) return () => {}
  if (prefersReducedMotion()) {
    window.scrollTo({ top: startY + target.getBoundingClientRect().top, behavior: 'instant' })
    return () => {}
  }
  const duration = (Number.isFinite(nativeDurationMs) && nativeDurationMs > 0
    ? nativeDurationMs : FALLBACK_DURATION_MS) / SCROLL_SPEED
  const started = performance.now()
  let frame: number | undefined
  let cancelled = false
  const cancel = () => {
    cancelled = true
    if (frame !== undefined) window.cancelAnimationFrame(frame)
    frame = undefined
    window.removeEventListener('wheel', cancel)
    window.removeEventListener('touchstart', cancel)
    window.removeEventListener('touchmove', cancel)
    window.removeEventListener('pointerdown', cancel)
    window.removeEventListener('keydown', onKey)
    document.removeEventListener('visibilitychange', onVisibility)
  }
  const onKey = (event: KeyboardEvent) => {
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) cancel()
  }
  const onVisibility = () => { if (document.hidden) cancel() }
  const tick = () => {
    frame = undefined
    if (cancelled) return
    const p = Math.min(1, Math.max(0, (performance.now() - started) / duration))
    const eased = p < 0.5 ? 4 * p ** 3 : 1 - (-2 * p + 2) ** 3 / 2
    const destination = window.scrollY + target.getBoundingClientRect().top
    window.scrollTo({ top: startY + (destination - startY) * eased, behavior: 'instant' })
    if (p < 1) frame = window.requestAnimationFrame(tick)
    else cancel()
  }
  window.addEventListener('wheel', cancel, { passive: true })
  window.addEventListener('touchstart', cancel, { passive: true })
  window.addEventListener('touchmove', cancel, { passive: true })
  window.addEventListener('pointerdown', cancel, { passive: true })
  window.addEventListener('keydown', onKey)
  document.addEventListener('visibilitychange', onVisibility)
  frame = window.requestAnimationFrame(tick)
  return cancel
}

/** 실제 화면을 움직이지 않고 동일 거리의 기본 smooth-scroll 시간을 측정한다. */
export function measureNativeScrollDuration(distance: number, onMeasured: (ms: number) => void): () => void {
  if (prefersReducedMotion() || !Number.isFinite(distance) || distance <= 1
    || typeof HTMLElement.prototype.scrollTo !== 'function') return () => {}
  const probe = document.createElement('div')
  probe.dataset.scrollCalibration = ''
  probe.setAttribute('aria-hidden', 'true')
  probe.inert = true
  const height = window.innerHeight || 800
  probe.style.cssText = `position:fixed;left:-10000px;top:0;width:1px;height:${height}px;overflow:scroll;pointer-events:none;overflow-anchor:none`
  const spacer = document.createElement('div')
  spacer.style.height = `${height + distance}px`
  probe.append(spacer)
  document.body.append(probe)
  const started = performance.now()
  let frame: number | undefined
  let finished = false
  const cancel = () => {
    finished = true
    if (frame !== undefined) window.cancelAnimationFrame(frame)
    window.clearTimeout(timeout)
    probe.remove()
  }
  const finish = (duration: number) => {
    if (finished) return
    cancel()
    onMeasured(duration >= 50 && duration <= 1200 ? duration : FALLBACK_DURATION_MS)
  }
  const tick = () => {
    frame = undefined
    if (finished) return
    if (Math.abs(probe.scrollTop - distance) <= 1) finish(performance.now() - started)
    else frame = window.requestAnimationFrame(tick)
  }
  const timeout = window.setTimeout(() => finish(FALLBACK_DURATION_MS), 1500)
  probe.scrollTo({ top: distance, behavior: 'smooth' })
  frame = window.requestAnimationFrame(tick)
  return cancel
}
